import React, { useState, useEffect, useCallback, useMemo } from "react";
import VMService from "../services/VMService";

const storeKey = process.env.REACT_APP_USER_KEY;
const tokenKey = storeKey + "_tk";
const permissionKey = storeKey + "_per";

export const AuthContext = React.createContext();

export const AuthContextProvider = ({ children }) => {

  const [session, setSession] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const metaSession = sessionStorage.getItem(storeKey);
    const tokenSession = sessionStorage.getItem(tokenKey);
    const permSession = sessionStorage.getItem(permissionKey);

    let combinedSession = {};

    if (metaSession) {
      const parsed = JSON.parse(metaSession);
      combinedSession = { ...combinedSession, ...parsed };
    }

    if (tokenSession) {
      const parsed = JSON.parse(tokenSession);
      combinedSession = { ...combinedSession, ...parsed };
    }

    if (Object.keys(combinedSession).length > 0) {
      setSession(combinedSession);
    }

    if (permSession) {
      const parsed = JSON.parse(permSession);
      setPermissions(parsed?.permissions || []);
    }

    setAuthLoading(false);
  }, []);

  const updateSessionStorage = useCallback((updatedFields) => {

    const newSession = { ...session, ...updatedFields };

    const metaPart = {
      instanceId: newSession.instanceId,
      userId: newSession.userId,
      roleId: newSession.roleId,
      isAnswered: newSession.isAnswered,
      resetPassword: newSession.resetPassword,
      mfaSetupCompleted: newSession.mfaSetupCompleted,
      mfaSetupEnabled: newSession.mfaSetupEnabled,
      mfaVerified: newSession.mfaVerified,
      onGoingValidations: newSession.onGoingValidations,
      displayName: newSession.displayName,
      courseCount: newSession.courseCount,
      impersonate: newSession.impersonate,
      impersonateUser: newSession.impersonateUser,
    };

    sessionStorage.setItem(storeKey, JSON.stringify(metaPart));
    setSession(newSession);

  }, [session]);

  const updatePermissionStorage = useCallback((perms) => {

    sessionStorage.setItem(
      permissionKey,
      JSON.stringify({ permissions: perms })
    );

    setPermissions(perms);

  }, []);

  const getPermissions = useCallback(
    (menuName) =>
      permissions.filter(
        (p) => p.menuName?.toLowerCase() === menuName?.toLowerCase()
      ),
    [permissions]
  );

  const login = useCallback((

    token,
    refresh_token,
    instanceId,
    userId,
    roleId,
    isAnswered = false,
    resetPassword = false,
    mfaSetupCompleted = false,
    mfaSetupEnabled = false,
    mfaVerified = false,
    onGoingValidations = false,
    displayName = "",
    perms = [],
    courseCount = 0,
    impersonate = false,
    impersonateUser = 0
  ) => {
    const tokenPart = {
      token,
      refreshToken: refresh_token,
    };

    const metaPart = {
      instanceId,
      userId,
      roleId,
      isAnswered,
      resetPassword,
      mfaSetupCompleted,
      mfaSetupEnabled,
      mfaVerified,
      onGoingValidations,
      displayName,
      courseCount,
      impersonate,
      impersonateUser,
    };
    setSession({ ...tokenPart, ...metaPart });
    sessionStorage.setItem(tokenKey, JSON.stringify(tokenPart));
    sessionStorage.setItem(storeKey, JSON.stringify(metaPart));
    sessionStorage.setItem(permissionKey, JSON.stringify({ permissions: perms }));
    setPermissions(perms);
    setAuthLoading(false);
  }, []);

 const logout = useCallback(async () => {

    const savedCourse = localStorage.getItem("running_lab_course");

if (savedCourse && session.token) {

  try {

    const course = JSON.parse(savedCourse);

 VMService.stopVM(course.id, session);

    localStorage.setItem(
      "vm_event",
      JSON.stringify({
        type: "STOP",
        time: Date.now(),
      })
    );

  } catch (err) {
    console.error("Failed to stop VM during logout", err);
  }
}

    setSession({});
    setPermissions([]);
    setAuthLoading(false);
    sessionStorage.removeItem(storeKey);
    sessionStorage.removeItem(tokenKey);
    sessionStorage.removeItem(permissionKey);
    sessionStorage.removeItem("hasShownInitialLoader");
    sessionStorage.removeItem("vm_data");
    localStorage.removeItem("lastActivity");
    // Cyber Range cleanup
    localStorage.removeItem("running_lab_course");
    localStorage.removeItem("lab_end_time");
    localStorage.removeItem("lab_last_activity");
    localStorage.removeItem("vm_event");
    localStorage.removeItem("vm_tab_reloading");
    localStorage.removeItem("lab_force_shutdown");

    localStorage.setItem("cyberBay_logout_event", Date.now());
    window.location.replace("#/login");
 }, [session]);

  const setAndStore = useCallback((key, value) => {
    updateSessionStorage({ [key]: value });
  }, [updateSessionStorage]);
    useEffect(() => {
    const syncLogout = (event) => {
      if (event.key === "cyberBay_logout_event") {
        setSession({});
        setPermissions([]);
        setAuthLoading(false);
        sessionStorage.removeItem(storeKey);
        sessionStorage.removeItem(tokenKey);
        sessionStorage.removeItem(permissionKey);

        if (!window.location.hash.includes("login")) {
          window.location.replace("#/login");
        }
      }
    };
    window.addEventListener("storage", syncLogout);
    return () => window.removeEventListener("storage", syncLogout);

  }, []);

  const contextValue = useMemo(() => ({
    token: session.token || "",
    refreshToken: session.refreshToken || "",
    instanceId: session.instanceId || 0,
    userId: session.userId || 0,
    roleId: session.roleId || 0,
    displayName: session.displayName || "",
    courseCount: session.courseCount || 0,
    impersonate: session.impersonate || false,
    impersonateUser: session.impersonateUser || 0,

    isAnswered: session.isAnswered || false,
    resetPassword: session.resetPassword || false,
    mfaSetupCompleted: session.mfaSetupCompleted || false,
    mfaSetupEnabled: session.mfaSetupEnabled || false,
    mfaVerified: session.mfaVerified || false,
    onGoingValidations: session.onGoingValidations || false,

    isLoggedIn: !!session.token,
    permissions,
    authLoading,

    login,
    logout,
    setAnswered: (flag) => setAndStore("isAnswered", flag),
    setResetPasswordCompleted: (flag) => setAndStore("resetPassword", flag),
    setMFAVerified: (flag) => setAndStore("mfaVerified", flag),
    setMFASetupCompleted: (flag) => setAndStore("mfaSetupCompleted", flag),
    setMFASetupEnabled: (flag) => setAndStore("mfaSetupEnabled", flag),
    setOnGoingValidations: (flag) => setAndStore("onGoingValidations", flag),
    setDisplayName: (name) => setAndStore("displayName", name),
    setPermissions: updatePermissionStorage,
    getPermissions,
  }), [session, permissions, authLoading, login, logout, setAndStore, updatePermissionStorage, getPermissions]);

  return (<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>);
};

export default AuthContext;